(ns ui.view.grid-opensearch.columns-modal.view
  "The Edit columns modal of the grid. It follows the modal of the UX mockup
   (Table view, three dots menu, Columns). The user sees the visible columns in
   groups, adds more columns from a list, and saves the choice. The classes come
   from the generated ZapKit styles. `dom-overrides` has the classes of the
   mockup that ZapKit does not have."
  (:require
   [common.localization :as loc]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.combobox.grouped-picker :as grouped-picker]
   [ui.components.icon-button.view :as icon-button]
   [ui.components.modal.view :as modal]
   [ui.components.sortable-list.view :as sortable-list]
   [ui.components.tab.view :as tab]
   [ui.dom-globals :as dg]
   [ui.icons :as icons]
   [ui.view.grid-opensearch.columns-model :as columns-model]
   [ui.view.grid-opensearch.columns-modal.event :as event]
   [ui.view.grid-opensearch.columns-modal.sub :as sub]
   [ui.view.grid-opensearch.sub :as grid-sub]))

(def dom-overrides
  "Classes of the mockup that ZapKit does not have. Each class string is copied
   from the DOM of the mockup. When ZapKit has the class, use it and delete the
   entry here.

   `:modal-size` is the width of the modal: the `md` width of ZapKit.
   `:modal-panel` and `:modal-body` let the list of the picker go over the edge of
   the modal, and give the panel the height of the mockup.
   `:panel` is the area of a tab. It has a fixed height, so the modal does not
   change its height when the user changes the tab.
   `:section-header`, `:section-label`, `:column-row`, `:handle`,
   `:handle-locked`, `:handle-movable`, `:label-wrap`, `:label`, `:sub-label`
   and `:message` are the lists of the columns."
  {:modal-size     "var(--modal-md)"
   :modal-panel    "tw2-max-h-[calc(100vh-4rem)] tw2-overflow-visible"
   :modal-body     "tw2-relative tw2-z-20 tw2-min-h-0 tw2-overflow-visible"
   :panel          "tw2-relative tw2-h-72 tw2-w-full tw2-shrink-0 tw2-overflow-visible"
   :section-header "tw2-mb-1 tw2-flex tw2-items-center tw2-gap-2 tw2-px-1 tw2-py-0.5"
   :section-label  "tw2-text-xs tw2-font-medium tw2-uppercase tw2-tracking-wide tw2-text-tertiary"
   :column-row     "tw2-flex tw2-items-center tw2-gap-2 tw2-rounded-dropdown tw2-border tw2-border-transparent tw2-px-1 tw2-py-0.5"
   :handle         "tw2-flex tw2-h-8 tw2-w-6 tw2-shrink-0 tw2-items-center tw2-justify-center tw2-text-tertiary"
   :handle-locked  "tw2-cursor-default tw2-opacity-40"
   :handle-movable "tw2-cursor-grab active:tw2-cursor-grabbing"
   :label-wrap     "tw2-min-w-0 tw2-flex-1"
   :label          "tw2-truncate tw2-text-sm tw2-text-primary"
   :sub-label      "tw2-truncate tw2-text-xs tw2-text-tertiary"
   :message        "tw2-text-sm tw2-text-tertiary"})

(defn column-label
  [column]
  (grid-sub/localize-header column))

(defn group-label
  "The label of a group of columns. The group `:root` has the name of the listed
   entity. Another group has the name of the field that the columns come through."
  [{:keys [group entity-type]}]
  (if (= :root group)
    (if entity-type
      (loc/tr entity-type)
      (loc/tr :grid-columns-root-group))
    (grid-sub/localize-header group)))

(defn section-rows
  "Split the columns of a section of the draft. The locked columns come first in
   a section. They cannot move. Return a map with `:locked-columns` and
   `:movable-columns`."
  [{:keys [section draft]}]
  (let [locked? (partial columns-model/locked? draft)]
    {:locked-columns  (vec (filter locked? (:columns section)))
     :movable-columns (vec (remove locked? (:columns section)))}))

(defn move-event
  "Return the event that moves a column in a section. The sortable list counts
   only the movable columns, and the model counts all the columns of the section,
   so the indexes start after the locked columns."
  [{:keys [grid-id section-index locked-count source-index destination-index]}]
  [::event/move-column grid-id {:section-index section-index
                                :from          (+ locked-count source-index)
                                :to            (+ locked-count destination-index)}])

(defn handle-view
  "The handle at the start of a row. A locked column has a lock. Another column
   has the grip that the user drags. A `reorder?` false greys the grip, because
   the section has no other column to change places with."
  [{:keys [locked? reorder?]}]
  [:div
   {:class       (str (:handle dom-overrides)
                      " "
                      (if (or locked? (not reorder?))
                        (:handle-locked dom-overrides)
                        (:handle-movable dom-overrides)))
    :aria-hidden true}
   (if locked?
     [icons/lock-icon {:class "tw2-h-4 tw2-w-4"}]
     [icons/grip-vertical-icon {:class "tw2-h-4 tw2-w-4"}])])

(defn locked-row
  [{:keys [column]}]
  [:div
   {:class (:column-row dom-overrides)}
   [handle-view {:locked?  true
                 :reorder? false}]
   [:div
    {:class (:label-wrap dom-overrides)}
    [:p
     {:class (:label dom-overrides)}
     (column-label column)]
    [:p
     {:class (:sub-label dom-overrides)}
     (loc/tr :grid-columns-always-visible)]]])

(defn movable-row
  [{:keys [grid-id column drag-handle]}]
  (let [label (column-label column)]
    [:div
     {:class (:column-row dom-overrides)}
     drag-handle
     [:div
      {:class (:label-wrap dom-overrides)}
      [:p
       {:class (:label dom-overrides)}
       label]]
     [icon-button/view
      {:aria-label (str (loc/tr :remove) " " label)
       :on-click   (fn [_]
                     (rf/dispatch [::event/remove-column grid-id column]))}
      [icons/x-icon
       {:class       (icon-button/icon-class :md)
        :aria-hidden true}]]]))

(defn section-view
  "A section of the visible columns: the label of its group, the locked columns
   and the columns that the user can drag."
  [{:keys [grid-id section section-index draft entity-type]}]
  (let [{:keys [locked-columns movable-columns]} (section-rows {:section section
                                                                :draft   draft})
        reorder? (< 1 (count movable-columns))]
    [:section
     {:class "tw2-rounded-dropdown tw2-border tw2-border-transparent"}
     [:div
      {:class (:section-header dom-overrides)}
      [:span
       {:class "tw2-w-6 tw2-shrink-0"
        :aria-hidden true}]
      [:p
       {:class (:section-label dom-overrides)}
       (group-label {:group       (:group section)
                     :entity-type entity-type})]]
     [:div
      {:class "tw2-flex tw2-flex-col tw2-gap-1"}
      (for [column locked-columns]
        ^{:key (pr-str column)}
        [locked-row {:column column}])
      (when (seq movable-columns)
        [sortable-list/view
         {:items              (mapv (fn [column]
                                      {:key    (pr-str column)
                                       :column column})
                                    movable-columns)
          :get-key            :key
          :drag-handle?       true
          :gap                4
          :render-drag-handle (fn [_]
                                [handle-view {:locked?  false
                                              :reorder? reorder?}])
          :render-item        (fn [{:keys [item drag-handle]}]
                                [movable-row {:grid-id     grid-id
                                              :column      (:column item)
                                              :drag-handle drag-handle}])
          :on-reorder         (fn [{:keys [source-index destination-index]}]
                                (rf/dispatch (move-event {:grid-id           grid-id
                                                          :section-index     section-index
                                                          :locked-count      (count locked-columns)
                                                          :source-index      source-index
                                                          :destination-index destination-index})))}])]]))

(defn visible-panel
  [{:keys [grid-id draft entity-type]}]
  (let [sections (columns-model/sections draft)]
    [:div
     {:role  "tabpanel"
      :class "tw2-flex tw2-h-full tw2-min-h-0 tw2-flex-col tw2-gap-3 tw2-overflow-y-auto"}
     (if (seq sections)
       (map-indexed (fn [index section]
                      ^{:key (str index)}
                      [section-view {:grid-id       grid-id
                                     :section       section
                                     :section-index index
                                     :draft         draft
                                     :entity-type   entity-type}])
                    sections)
       [:p
        {:class (:message dom-overrides)}
        (loc/tr :grid-columns-none-selected)])]))

(defn picker-groups
  "The hidden columns of the draft as the groups of the picker."
  [{:keys [draft entity-type]}]
  (mapv (fn [{:keys [group columns]}]
          {:key   group
           :label (group-label {:group       group
                                :entity-type entity-type})
           :items (mapv (fn [column]
                          {:value column
                           :label (column-label column)})
                        columns)})
        (columns-model/hidden-groups draft)))

(defn add-panel
  [{:keys [grid-id draft entity-type]}]
  [:div
   {:role  "tabpanel"
    :class "tw2-flex tw2-h-full tw2-flex-col tw2-gap-1 tw2-overflow-visible"}
   [grouped-picker/view
    {:trigger-label (loc/tr :grid-columns-select-from-list)
     :groups        (picker-groups {:draft       draft
                                    :entity-type entity-type})
     :labels        {:search-placeholder (loc/tr :search)
                     :empty              (loc/tr :grid-columns-all-visible)
                     :empty-search       (loc/tr :grid-columns-no-match)
                     :expand-group       (loc/tr :expand)
                     :collapse-group     (loc/tr :collapse)}
     :on-select     (fn [column]
                      (rf/dispatch [::event/add-column grid-id column]))}]])

(defn configuration-panel
  []
  [:div
   {:role  "tabpanel"
    :class "tw2-flex tw2-h-full tw2-flex-col tw2-gap-3 tw2-overflow-y-auto"}
   [:p
    {:class (:message dom-overrides)}
    (loc/tr :grid-columns-no-options)]])

(defn tab-items
  []
  [{:value :visible
    :label (loc/tr :grid-columns-tab-visible)}
   {:value :add
    :label (loc/tr :grid-columns-tab-add)}
   {:value     :configuration
    :label     (loc/tr :configuration)
    :disabled? true}])

(defn body-view
  "The body of the modal: the tabs and the area of the tab that is selected.
   `state` is the state of the modal, a map with `:tab`, `:entity-type` and
   `:draft`."
  [{:keys [grid-id state]}]
  (let [{:keys [tab draft entity-type]} state
        panel-props {:grid-id     grid-id
                     :draft       draft
                     :entity-type entity-type}]
    [:div
     {:class "tw2-flex tw2-flex-col tw2-gap-4"}
     [tab/view
      {:items      (tab-items)
       :value      tab
       :on-change  (fn [next-tab]
                     (rf/dispatch [::event/select-tab grid-id next-tab]))
       :aria-label (loc/tr :grid-columns-sections)}]
     [:div
      {:class (:panel dom-overrides)}
      (case tab
        :add [add-panel panel-props]
        :configuration [configuration-panel]
        [visible-panel panel-props])]]))

(defn footer-view
  [{:keys [grid-id]}]
  [:div
   {:class "tw2-flex tw2-w-full tw2-justify-end"}
   [button/component
    {:variant  :primary
     :size     :sm
     :on-click (fn [_]
                 (rf/dispatch [::event/save grid-id]))}
    (loc/tr :save-changes)]])

(defn open-modal-view
  "The open modal. It closes with the key Escape, the overlay and the close button.
   A listener of the document watches the key while the modal is open."
  [{:keys [grid-id state]}]
  (r/with-let [close! (fn [& _]
                        (rf/dispatch [::event/close grid-id]))
               escape-handler (fn [event]
                                (dg/escape-key?-handler event close!))
               _ (dg/add-event-listener! js/document "keydown" escape-handler)]
    [modal/view
     {:visible?          true
      :variant           :tw2-styles
      :size              (:modal-size dom-overrides)
      :hide!             close!
      :close-button?     true
      :helper-text       (loc/tr :grid-columns-helper)
      :content-class     (:modal-panel dom-overrides)
      :body-class        (:modal-body dom-overrides)
      :on-overlay-click  close!}
     (loc/tr :grid-columns-edit)
     [body-view {:grid-id grid-id
                 :state   state}]
     [footer-view {:grid-id grid-id}]]
    (finally
      (dg/remove-event-listener! js/document "keydown" escape-handler))))

(defn modal-view
  "The Edit columns modal of a grid. It shows when the state of the grid has the
   modal, see `ui.view.grid-opensearch.columns-modal.event/open-modal`."
  [{:keys [grid-id]}]
  (when-let [state @(rf/subscribe [::sub/modal grid-id])]
    [open-modal-view {:grid-id grid-id
                      :state   state}]))
